import { useEffect, useMemo, useRef, useState } from "react";
import { createUser, deleteUser, getRoles, getUsers, getUserSites, updateUser } from "../api";
import ManagementFilterHeader, { formatManagementSearchInput, matchesManagementSearch, ManagementListLoading, makeManagementFilterOptions, matchesManagementFilter, sortManagementRows } from "../components/management/ManagementFilterHeader";
import useIncrementalRows from "../hooks/useIncrementalRows";
import ImportExportOverlay from "../components/management/ImportExportOverlay";
import VestaSelect from "../components/common/VestaSelect";
import { OverlayFieldLabel } from "../components/common/OverlayValidation";
import { getUserPageLevel, readStoredUser, UNAUTHORIZED_LABEL } from "../utils/permissions";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/users.css";

const EMPTY_USER = { siteIds: [], defaultSiteId: "", username: "", fullName: "", email: "", roleId: "", password: "", active: true, unlock: false };
const EMPTY_FILTERS = { fullName: [], username: [], email: [], roleDescription: [], lastLoginAt: [] };

function resolveRoleId(value, roles = []) {
  const token = String(value || "").trim().toLowerCase();
  if (!token) return "";
  return roles.find((role) => [role._id, role.description].some((item) => String(item || "").trim().toLowerCase() === token))?._id || String(value || "").trim();
}

function makeUserImportExportFields(roles = []) {
  return [
    { key: "username", label: "Username", required: true },
    { key: "fullName", label: "Nome completo", required: true, aliases: ["Nome"] },
    { key: "email", label: "Email" },
    { key: "roleId", label: "Ruolo", required: true, aliases: ["Role", "Profilo"], parse: (value) => resolveRoleId(value, roles), exportValue: (user) => user.roleDescription || user.roleId || "" },
    { key: "password", label: "Password", required: true, importOnly: true },
    { key: "siteIds", label: "Sedi abilitate", exportValue: (user) => (user.siteIds || []).join(", "), importOnly: false },
    { key: "defaultSiteId", label: "Sede predefinita" },
    { key: "active", label: "Attivo", type: "boolean", aliases: ["Stato"] },
  ];
}

function displayValue(value) {
  const text = String(value || "").trim();
  return text || "-";
}

function toDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("it-IT");
}

function OptionsMenu({ open, menuRef, disabled, confirming, canEdit, canDelete, blockedClass, onBlocked, onToggle, onNew, onReset, onImport, onExport, onStartConfirm, onCancelConfirm, onConfirm }) {
  return (
    <div className="management_options_menu" ref={menuRef}>
      <button type="button" className="management_options_button" onClick={onToggle} aria-label="Opzioni utenti" aria-expanded={open}>
        <span></span><span></span><span></span>
      </button>
      {open && (
        <div className="management_options_dropdown">
          <button type="button" className={`management_options_item${blockedClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canEdit ? onNew : onBlocked}>Nuovo Utente</button>
          <button type="button" className="management_options_item" onClick={onReset}>Azzera ricerca e filtri</button>
          <button type="button" className={`management_options_item${blockedClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canEdit ? onImport : onBlocked}>Importa Valori</button>
          <button type="button" className="management_options_item" onClick={onExport}>Esporta Valori</button>
          {confirming ? (
            <div className="management_options_confirm_row">
              <button type="button" className="management_options_item is_neutral" onClick={onCancelConfirm}>Annulla</button>
              <button type="button" className={`management_options_item is_danger${blockedClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canDelete ? onConfirm : onBlocked}>Conferma</button>
            </div>
          ) : (
            <button type="button" className={`management_options_item is_danger${blockedClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={disabled} onClick={canDelete ? onStartConfirm : onBlocked}>Eliminazione Multipla</button>
          )}
        </div>
      )}
    </div>
  );
}

function UserOverlay({ draft, roles, sites, error, onChange, onClose, onSave }) {
  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_overlay_card" role="dialog" aria-modal="true" aria-label="Utente">
        <div className="overlay_header"><h2>{draft._id ? "MODIFICA UTENTE" : "NUOVO UTENTE"}</h2><button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button></div>
        <div className="overlay_body">
          <div className="overlay_section">
            <h3>Dati accesso</h3>
            <div className="overlay_fields_grid">
              <label className="overlay_field"><OverlayFieldLabel required>Username</OverlayFieldLabel><input value={draft.username || ""} onChange={(e) => onChange("username", e.target.value)} /></label>
              <label className="overlay_field"><OverlayFieldLabel required>Nome completo</OverlayFieldLabel><input value={draft.fullName || ""} onChange={(e) => onChange("fullName", e.target.value)} /></label>
              <label className="overlay_field"><span>Email</span><input type="email" value={draft.email || ""} onChange={(e) => onChange("email", e.target.value)} /></label>
              <label className="overlay_field"><OverlayFieldLabel required>Ruolo</OverlayFieldLabel><VestaSelect value={draft.roleId || ""} onChange={(e) => onChange("roleId", e.target.value)}><option value="">Seleziona</option>{roles.map((role) => <option key={role._id} value={role._id}>{role.description}</option>)}</VestaSelect></label>
              <label className="overlay_field"><OverlayFieldLabel required={!draft._id}>{draft._id ? "Nuova password" : "Password"}</OverlayFieldLabel><input type="password" value={draft.password || ""} onChange={(e) => onChange("password", e.target.value)} minLength={8} pattern="(?=.*[A-Za-z])(?=.*[0-9]).{8,}" required={!draft._id} placeholder={draft._id ? "Lascia vuota per non cambiare" : "Minimo 8 caratteri con numeri"} /></label>
              {draft.loginLocked && <label className="overlay_field overlay_check_field booking_invoice_same_field"><span>Sblocca login</span><input type="checkbox" checked={draft.unlock === true} onChange={(e) => onChange("unlock", e.target.checked)} /></label>}
            </div>
          </div>
          <div className="overlay_section users_sites_section">
            <h3>Abilitazioni sedi</h3>
            <p>Seleziona le sedi visibili all'utente e la sede predefinita per il planning.</p>
            <div className="users_sites_choices" role="group" aria-label="Sedi abilitate">
              {sites.map((site) => {
                const selected = (draft.siteIds || []).includes(site._id);
                return <label className="users_site_choice" key={site._id}>
                  <input type="checkbox" checked={selected} onChange={(event) => {
                    const next = event.target.checked
                      ? [...new Set([...(draft.siteIds || []), site._id])]
                      : (draft.siteIds || []).filter((id) => id !== site._id);
                    onChange("siteIds", next);
                    if (!next.includes(draft.defaultSiteId)) onChange("defaultSiteId", next[0] || "");
                  }} />
                  <span>{site.description}</span>
                </label>;
              })}
              {!sites.length && <span>Nessuna sede attiva disponibile.</span>}
            </div>
            <label className="overlay_field users_default_site_field">
              <OverlayFieldLabel required>Sede predefinita</OverlayFieldLabel>
              <VestaSelect value={draft.defaultSiteId || ""} onChange={(event) => onChange("defaultSiteId", event.target.value)}>
                <option value="">Seleziona una sede</option>
                {sites.filter((site) => (draft.siteIds || []).includes(site._id)).map((site) => <option key={site._id} value={site._id}>{site.description}</option>)}
              </VestaSelect>
            </label>
          </div>
        </div>
        <div className="overlay_footer"><div className="overlay_error_message">{error}</div><button type="button" className="overlay_save_button overlay_save_close_button" onClick={onSave}>Salva e Chiudi</button></div>
      </div>
    </div>
  );
}

export default function Users() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(EMPTY_USER);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayError, setOverlayError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(EMPTY_FILTERS);
  const [sortState, setSortState] = useState({ key: "username", direction: "asc" });
  const [importExportMode, setImportExportMode] = useState("");
  const optionsMenuRef = useRef(null);
  const filterPanelRef = useRef(null);
  const pagePermissionLevel = getUserPageLevel(readStoredUser(), "user");
  const canEditPage = pagePermissionLevel >= 2;
  const canDeletePage = pagePermissionLevel >= 3;
  const blockedActionClass = (allowed) => allowed ? "" : " is_permission_blocked";
  const blockAction = () => {};

  async function load() {
    setLoading(true);
    setError("");
    try {
      const usersData = await getUsers({ includeInactive: true });
      let rolesData = { items: [] };
      let sitesData = { items: [] };
      if (canEditPage) {
        sitesData = await getUserSites();
        try {
          rolesData = await getRoles({ includeInactive: true });
        } catch {
          rolesData = { items: [] };
        }
      }
      setItems(Array.isArray(usersData?.items) ? usersData.items : []);
      setRoles(Array.isArray(rolesData?.items) ? rolesData.items.filter((role) => role.active !== false) : []);
      setSites(Array.isArray(sitesData?.items) ? sitesData.items.filter((site) => site.active !== false) : []);
    } catch {
      setError("Impossibile caricare gli utenti");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    function closeOptions(event) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) {
        setOptionsOpen(false);
        setConfirmBulkDelete(false);
      }
    }
    document.addEventListener("pointerdown", closeOptions);
    return () => document.removeEventListener("pointerdown", closeOptions);
  }, [optionsOpen]);

  useEffect(() => {
    if (!activeFilter) return undefined;
    function closeFilters(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) setActiveFilter("");
    }
    document.addEventListener("pointerdown", closeFilters);
    return () => document.removeEventListener("pointerdown", closeFilters);
  }, [activeFilter]);

  useEffect(() => { if (selectedIds.length === 0) setConfirmBulkDelete(false); }, [selectedIds]);

  const filterOptions = useMemo(() => ({
    fullName: makeManagementFilterOptions(items, (item) => item.fullName || item.username),
    username: makeManagementFilterOptions(items, (item) => item.username),
    email: makeManagementFilterOptions(items, (item) => item.email),
    roleDescription: makeManagementFilterOptions(items, (item) => item.roleDescription),
    lastLoginAt: makeManagementFilterOptions(items, (item) => toDate(item.lastLoginAt)),
  }), [items]);

  const userImportExportFields = useMemo(() => makeUserImportExportFields(roles), [roles]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const dateText = toDate(item.lastLoginAt);
      const matchesSearch = matchesManagementSearch([item.username, item.fullName, item.email, item.roleDescription, dateText], search);
      if (!matchesSearch) return false;
      return matchesManagementFilter(item.fullName || item.username, columnFilters.fullName, "text")
        && matchesManagementFilter(item.username, columnFilters.username, "text")
        && matchesManagementFilter(item.email, columnFilters.email, "text")
        && matchesManagementFilter(item.roleDescription, columnFilters.roleDescription)
        && matchesManagementFilter(item.lastLoginAt, columnFilters.lastLoginAt, "date");
    });
  }, [items, search, columnFilters]);

  const userSortGetters = useMemo(() => ({
    username: { getter: (item) => item.username, type: "text" },
    fullName: { getter: (item) => item.fullName || item.username, type: "text" },
    roleDescription: { getter: (item) => item.roleDescription, type: "text" },
    email: { getter: (item) => item.email, type: "text" },
    lastLoginAt: { getter: (item) => item.lastLoginAt, type: "date" },
  }), []);
  const orderedFiltered = useMemo(() => sortManagementRows(filtered, sortState, userSortGetters), [filtered, sortState, userSortGetters]);

  function toggleFilter(columnKey) { setActiveFilter((current) => (current === columnKey ? "" : columnKey)); }
  function updateCheckFilter(columnKey, value, checked) { setColumnFilters((current) => { const values = Array.isArray(current[columnKey]) ? current[columnKey] : []; return { ...current, [columnKey]: checked ? [...values, value] : values.filter((item) => item !== value) }; }); }
  function clearColumnFilter(columnKey) { setColumnFilters((current) => ({ ...current, [columnKey]: [] })); }
  function updateRangeFilter(columnKey, value) { setColumnFilters((current) => ({ ...current, [columnKey]: value })); }
  function toggleSort(columnKey) { setSortState((current) => ({ key: columnKey, direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc" })); }
  function openUser(item = null) {
    if (!canEditPage) { blockAction(); return; }
    setOverlayError(""); setConfirmDeleteId(""); setOptionsOpen(false); setConfirmBulkDelete(false);
    const assigned = Array.isArray(item?.siteIds) ? item.siteIds : sites.map((site) => site._id);
    const defaultId = assigned.includes(item?.defaultSiteId) ? item.defaultSiteId : (assigned[0] || "");
    setDraft({ ...EMPTY_USER, ...(item || {}), siteIds: assigned, defaultSiteId: defaultId, password: "", unlock: false });
    setShowOverlay(true);
  }
  function updateDraft(field, value) { setDraft((current) => ({ ...current, [field]: value })); }

  async function saveUser() {
    if (!canEditPage) { setOverlayError(UNAUTHORIZED_LABEL); return; }
    setOverlayError("");
    if (!draft.username || !draft.fullName || !draft.roleId || (!draft._id && !draft.password)) return setOverlayError("Completa i Dati Richiesti");
    if (!draft.siteIds?.length || !draft.siteIds.includes(draft.defaultSiteId)) return setOverlayError("Abilita almeno una sede e seleziona una sede predefinita");
    try {
      const payload = { ...draft };
      const data = payload._id ? await updateUser(payload._id, payload) : await createUser(payload);
      const saved = data?.item;
      if (saved) setItems((current) => current.some((item) => item._id === saved._id) ? current.map((item) => item._id === saved._id ? saved : item) : [...current, saved]);
      setShowOverlay(false);
    } catch (err) {
      setOverlayError(err?.response?.data?.error || "Dati Errati");
    }
  }

  function openImportExport(mode) { if (mode === "import" && !canEditPage) { blockAction(); return; } setImportExportMode(mode); setOptionsOpen(false); setConfirmBulkDelete(false); }

  async function importUsers(records) {
    if (!canEditPage) throw new Error(UNAUTHORIZED_LABEL);
    let imported = 0;
    for (const record of records) {
      try {
        const parsedIds = typeof record.siteIds === "string" ? record.siteIds.split(",").map((id) => id.trim()).filter(Boolean) : record.siteIds;
        const matchedIds = Array.isArray(parsedIds) && parsedIds.length ? parsedIds.map((token) => sites.find((site) => site._id === token || site.description.toLowerCase() === token.toLowerCase())?._id || token) : sites.map((site) => site._id);
        const defaultSiteId = sites.find((site) => site._id === record.defaultSiteId || site.description.toLowerCase() === String(record.defaultSiteId || "").toLowerCase())?._id || matchedIds[0] || "";
        const data = await createUser({ ...EMPTY_USER, ...record, siteIds: matchedIds, defaultSiteId });
        const saved = data?.item;
        if (saved) setItems((current) => current.some((item) => item._id === saved._id) ? current.map((item) => item._id === saved._id ? saved : item) : [...current, saved]);
        imported += 1;
      } catch (err) {
        throw new Error(`Riga ${imported + 2}: ${err?.response?.data?.error || "Dati Errati"}`);
      }
    }
    return { imported };
  }

  async function toggleActive(user) {
    if (!canEditPage) { blockAction(); return; }
    try {
      const data = await updateUser(user._id, { ...user, active: user.active === false });
      const saved = data?.item;
      if (saved) setItems((current) => current.map((item) => item._id === saved._id ? saved : item));
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile aggiornare lo stato utente");
    }
  }

  async function removeUser(user) {
    if (!canDeletePage) { blockAction(); setConfirmDeleteId(""); return; }
    try {
      await deleteUser(user._id);
      setItems((current) => current.filter((item) => item._id !== user._id));
      setSelectedIds((current) => current.filter((id) => String(id) !== String(user._id)));
      setConfirmDeleteId("");
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile eliminare l'utente");
    }
  }

  async function removeSelected() {
    if (selectedIds.length === 0) return;
    if (!canDeletePage) { blockAction(); setConfirmBulkDelete(false); return; }
    try {
      await Promise.all(selectedIds.map((id) => deleteUser(id)));
      setItems((current) => current.filter((item) => !selectedIds.some((id) => String(id) === String(item._id))));
      setSelectedIds([]);
      setConfirmBulkDelete(false);
      setOptionsOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile eliminare gli utenti selezionati");
    }
  }

  function resetSearchAndFilters() { setSearch(""); setColumnFilters(EMPTY_FILTERS); setSortState({ key: "username", direction: "asc" }); setActiveFilter(""); setOptionsOpen(false); setConfirmBulkDelete(false); }
  function toggleSelected(id, checked) { setSelectedIds((current) => checked ? [...new Set([...current, id])] : current.filter((item) => String(item) !== String(id))); }
  function toggleAllFiltered(checked) { const ids = orderedFiltered.map((item) => item._id); setSelectedIds((current) => checked ? [...new Set([...current, ...ids])] : current.filter((id) => !ids.some((itemId) => String(itemId) === String(id)))); }

  const ids = orderedFiltered.map((item) => item._id);
  const allFilteredSelected = ids.length > 0 && ids.every((id) => selectedIds.some((item) => String(item) === String(id)));
  const someFilteredSelected = ids.some((id) => selectedIds.some((item) => String(item) === String(id)));
  const filterProps = { activeFilter, onToggle: toggleFilter, onCheckChange: updateCheckFilter, onClear: clearColumnFilter, onRangeChange: updateRangeFilter, onSort: toggleSort };
  const incrementalRows = useIncrementalRows(orderedFiltered);

  return (
    <section className="window_page bookings_page management_page management_shared_page users_page">
      <div className="window_banner"><div className="window_banner_copy"><h1>UTENTI</h1><p>Gestione accessi, ruoli, stato utente, reset password e sblocco login.</p></div></div>
      <div className="management_list_panel">
        <div className="booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_shared_toolbar">
          <OptionsMenu open={optionsOpen} menuRef={optionsMenuRef} disabled={selectedIds.length === 0} confirming={confirmBulkDelete} canEdit={canEditPage} canDelete={canDeletePage} blockedClass={blockedActionClass} onBlocked={blockAction} onToggle={() => setOptionsOpen((current) => !current)} onNew={() => openUser()} onReset={resetSearchAndFilters} onImport={() => openImportExport("import")} onExport={() => openImportExport("export")} onStartConfirm={() => setConfirmBulkDelete(true)} onCancelConfirm={() => setConfirmBulkDelete(false)} onConfirm={removeSelected} />
          <input className="management_search" value={search} onChange={(e) => setSearch(formatManagementSearchInput(e.target.value))} placeholder="Cerca utenti (es. Mario, Rossi)" />
          <div className="management_toolbar_results"><strong className="management_results_count">Risultati: {filtered.length}</strong></div>
        </div>
        <div className="management_board management_table_board">
          <div className="management_table_shell" role="table" aria-label="Elenco utenti" ref={incrementalRows.shellRef} onScroll={incrementalRows.handleScroll}>
            <div className="management_table_header management_shared_table_header management_users_table_header" role="row" ref={filterPanelRef}>
              <div className="management_table_select_head" role="columnheader"><input type="checkbox" checked={allFilteredSelected} ref={(input) => { if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected; }} onChange={() => toggleAllFiltered(!someFilteredSelected)} aria-label="Seleziona tutti gli utenti filtrati" /></div>
              <ManagementFilterHeader columnKey="username" label="Username" value={columnFilters.username} filterType="text" sortDirection={sortState.key === "username" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="fullName" label="Nome" value={columnFilters.fullName} filterType="text" sortDirection={sortState.key === "fullName" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="roleDescription" label="Ruolo" options={filterOptions.roleDescription} value={columnFilters.roleDescription} sortDirection={sortState.key === "roleDescription" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="email" label="Email" value={columnFilters.email} filterType="text" sortDirection={sortState.key === "email" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="lastLoginAt" label="Ultimo login" value={columnFilters.lastLoginAt} filterType="date" sortDirection={sortState.key === "lastLoginAt" ? sortState.direction : ""} {...filterProps} />
              <div className="management_table_actions_head" role="columnheader">Azioni</div>
            </div>
            {loading && <ManagementListLoading label="Caricamento Utenti" />}
            {!loading && error && <div className="management_table_visible_status is_error">{error}</div>}
            {!loading && !error && filtered.length === 0 && <div className="management_table_visible_status">Nessun Risultato Trovato</div>}
            <div className="management_table_body" role="rowgroup">
              {incrementalRows.rows.map((user) => {
                const isConfirming = String(confirmDeleteId) === String(user._id);
                return (
                  <article key={user._id} className={`management_table_row management_users_table_row${user.active === false ? " is_inactive" : ""}${selectedIds.some((id) => String(id) === String(user._id)) ? " is_selected" : ""}`} data-vesta-focus-id={user._id} role="row">
                    <div className="management_table_cell management_table_select_cell" role="cell"><input type="checkbox" checked={selectedIds.some((id) => String(id) === String(user._id))} onChange={(event) => toggleSelected(user._id, event.target.checked)} aria-label={`Seleziona utente ${user.username}`} /></div>
                    <div className="management_table_cell management_table_title_cell" role="cell"><strong>{displayValue(user.username)}</strong></div>
                    <div className="management_table_cell" role="cell">{displayValue(user.fullName)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(user.roleDescription)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(user.email)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(toDate(user.lastLoginAt))}</div>
                    <div className="management_table_actions management_status_actions" role="cell">
                      <button type="button" className={`management_action_button ${user.active === false ? "management_action_success" : "management_action_danger"}${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => toggleActive(user)}>{user.active === false ? "Attiva" : "Disattiva"}</button>
                      {isConfirming ? <><button type="button" className="management_action_button management_action_cancel" onClick={() => setConfirmDeleteId("")}>Annulla</button><button type="button" className={`management_action_button management_action_confirm${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => removeUser(user)}>Conferma</button></> : <><button type="button" className={`management_action_button management_action_primary${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openUser(user)}>Modifica</button><button type="button" className={`management_action_button management_action_danger${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => canDeletePage ? setConfirmDeleteId(user._id) : blockAction()}>Elimina</button></>}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {showOverlay && <UserOverlay draft={draft} roles={roles} sites={sites} error={overlayError} onChange={updateDraft} onClose={() => setShowOverlay(false)} onSave={saveUser} />}
      {importExportMode && <ImportExportOverlay mode={importExportMode} title="Utenti" entityLabel="Utenti" fileBaseName="vesta_utenti" fields={userImportExportFields} rows={orderedFiltered} onClose={() => setImportExportMode("")} onImport={importUsers} />}
    </section>
  );
}
